import { default as getMoveKind, MoveKind } from './getMoveKind';
import handleBlockActionForShadowMailbox from '../../helpers/handleBlockActionForShadowMailbox';
import { DatapointStatus, returnTopExecutingActionDatapoint } from 'owa-analytics';
import { ActionSource } from 'owa-analytics-actions';
import folderStore, { Folder } from 'owa-folders';
import { showNotificationOnMoveFailure } from 'owa-mail-filterable-menu-behavior';
import { isSharedFolderItemMovementPermissible } from 'owa-mail-filterable-menu-behavior';
import { MailRowDataPropertyGetter, TableQueryType, TableView } from 'owa-mail-list-store';
import { lazyAddFolderIdToMoveToMruList } from 'owa-mail-move-mru';
import { getFolderIdFromTableView } from 'owa-mail-search';
import { InstrumentationContext } from 'owa-mail-store';
import * as undoActions from 'owa-mail-undo';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { select } from 'satcheljs/lib/legacy';
import {
    lazyMoveSharedFolderItems,
    lazyMoveSharedFolderConversations,
} from 'owa-mail-triage-action';
import logUserMailInteraction from '../../logUserMailInteraction';

export interface MoveMailListRowsHelperState {
    sourceFolder: Folder;
    destinationFolder: Folder;
}

/**
 * Handler for move the rows
 */
export type moveRowsInternalHandler = (
    rowKeys: string[],
    destinationFolderId: string,
    originalTableView: TableView,
    actionSource: ActionSource
) => Promise<void>;

/**
 * Move rows from table action
 * @param rowKeys - the row keys of the rows that need to be moved
 * @param destinationFolderId - the folder they need to go to
 * @param originalTableView - the table view the rows are moving from
 */
export default select<MoveMailListRowsHelperState>({
    sourceFolder: (rowKeys: string[], destinationFolderId: string, originalTableView: TableView) =>
        folderStore.folderTable.get(originalTableView.tableQuery.folderId),

    destinationFolder: (
        rowKeys: string[],
        destinationFolderId: string,
        originalTableView: TableView
    ) => folderStore.folderTable.get(destinationFolderId),
})(function moveMailListRowsHelper(
    rowKeys: string[],
    destinationFolderId: string,
    originalTableView: TableView,
    moveRowsInternal: moveRowsInternalHandler,
    actionSource: ActionSource,
    state?: MoveMailListRowsHelperState
): Promise<void> {
    const { sourceFolder, destinationFolder } = state;
    const moveKind = getMoveKind(sourceFolder, destinationFolder);
    const sourceFolderId = originalTableView.tableQuery.folderId;

    if (handleBlockActionForShadowMailbox(sourceFolderId, destinationFolderId)) {
        const moveDatapoint = returnTopExecutingActionDatapoint();
        moveDatapoint &&
            moveDatapoint.endWithError(
                DatapointStatus.UserError,
                new Error('Action not supported for shadow mailbox')
            );
        return Promise.resolve();
    }
    undoActions.clearLastUndoableAction();

    // Since row may have been deleted because moveRows is an lazy action,
    // we need to validate the row keys again before performing move
    // Here we cannot compare destinationFolderId with sourceFolderId, because sourceFolderId is null in Search,
    // so we need to get the actual parentFolderId of each row
    const filteredRowKeys = rowKeys.filter(
        rowKey =>
            originalTableView.rowKeys.indexOf(rowKey) >= 0 &&
            destinationFolderId !=
                MailRowDataPropertyGetter.getParentFolderId(rowKey, originalTableView)
    );

    const instrumentationContexts: InstrumentationContext[] = [];
    const isSearch = originalTableView.tableQuery.type == TableQueryType.Search;
    if (isSearch) {
        filteredRowKeys.forEach(rowKey => {
            const instrumentationContext = MailRowDataPropertyGetter.getInstrumentationContext(
                rowKey,
                originalTableView
            );
            if (instrumentationContext) {
                instrumentationContexts.push(instrumentationContext);
            }
        });
    }

    logUserMailInteraction(
        filteredRowKeys,
        originalTableView,
        instrumentationContexts,
        'Move',
        destinationFolderId
    );
    // We do not have any conversations that can be moved
    if (filteredRowKeys.length == 0) {
        const moveDatapoint = returnTopExecutingActionDatapoint();
        moveDatapoint &&
            moveDatapoint.endWithError(
                DatapointStatus.UserError,
                new Error('no conversations to move')
            );
        return Promise.resolve();
    }

    let promiseToReturn;
    switch (moveKind) {
        case MoveKind.SharedFolder:
            // Shared folder permission check for move action by drag and drop, if any of the source or destination folder
            // is shared folder with read-only permission, return immediately showing a failure notification
            if (
                !isSharedFolderItemMovementPermissible(
                    getFolderIdFromTableView(originalTableView),
                    destinationFolderId
                )
            ) {
                showNotificationOnMoveFailure(destinationFolderId);
                return Promise.resolve();
            }
            switch (originalTableView.tableQuery.listViewType) {
                case ReactListViewType.Conversation:
                    (async () => {
                        const moveSharedFolderConversations = await lazyMoveSharedFolderConversations.import();
                        moveSharedFolderConversations(
                            filteredRowKeys,
                            destinationFolderId,
                            originalTableView
                        );
                    })();
                    break;
                case ReactListViewType.Message:
                    (async () => {
                        const moveSharedFolderItems = await lazyMoveSharedFolderItems.import();
                        moveSharedFolderItems(
                            filteredRowKeys,
                            originalTableView.id,
                            destinationFolderId,
                            sourceFolderId
                        );
                    })();
                    break;
            }
            break;
        case MoveKind.Normal:
        default:
            // Update Move to mru list
            lazyAddFolderIdToMoveToMruList.importAndExecute(destinationFolderId);

            // Make service request
            promiseToReturn = moveRowsInternal(
                filteredRowKeys,
                destinationFolderId,
                originalTableView,
                actionSource
            );
            break;
    }
    return promiseToReturn;
});
